import { Component, ElementRef, Input, Output, ViewChild, EventEmitter } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { translate } from '@ngneat/transloco';
import { UploadModel } from 'src/app/shared/models/upload.model';
import { GroupModule } from 'src/app/shared/models/group-module.model';
import { ModuleMaterial } from 'src/app/shared/models/module-materials.model';
import { ApiValidation } from '../services/api-validation.service';
import { GroupService } from '../services/group.service';

export interface UploadComponentQueueType<T> {
    index: number;
    file: File;
    chunks: number;
    chunk: number;
    error: any;
    response: {data: T};
};


export interface UploadComponentQueue {
    index: number;
    file: File;
    chunks: number;
    chunk: number;
    error: any;
    response: {data: UploadModel};
};

export interface MaterialFileInfo {
    name: string;
    url: string;
    mime: string;
};

@Component({
  selector: 'shared-modal-material',
  templateUrl: './modal-material.component.html',
  styleUrls: ['./modal-material.component.scss']
})
export class SharedModalMaterialComponent {
    @Output() onAction: EventEmitter<{action: string}> = new EventEmitter();
    @Input() groupId: number = 0;
    @Input() module: GroupModule|null = null;
    @Input() mode: ('add'|'edit') = 'add';
    @Input() materialId: number = 0;

    protected subscriptions: Subscription[] = [];
    protected innerLoadingQueue: number = 0;

    form: UntypedFormGroup|null = null;
    errors: any = {};
    loading: boolean = false;
    message: string = '';
    messageType: ('error'|'success'|'warning') = 'error';
    messageFiles: string = '';
    messageFilesType: ('error'|'success'|'warning') = 'error';
    currentFile: MaterialFileInfo | null = null;
    material: ModuleMaterial| null = null;



    set loadingQueue(value: number) {
        if (this.innerLoadingQueue !== value) {
            this.innerLoadingQueue = value;

            if (this.innerLoadingQueue <= 0) {
                this.innerLoadingQueue = 0;
                this.loading = false;
            } else {
                this.loading = true;
            }
        }
    }
    get loadingQueue(): number {
        return this.innerLoadingQueue;
    }

    constructor(
        private messageService: MessageService,
        private apiValidation: ApiValidation,
        private groupService: GroupService,
    ) { }

    ngOnInit(): void {
        if (this.mode === 'edit' && this.materialId) {
            this.extractMaterialInfo()
        }
        this.initForm();
    }

    onCloseAction(e?: any): void {
        this.onAction.emit({action: 'close'});
    }

    protected initForm() {
        this.form = new UntypedFormGroup({
            title: new UntypedFormControl(this.material?.title ?? '', [Validators.required, Validators.maxLength(255)]),
            description: new UntypedFormControl(this.material?.description ?? '', [Validators.maxLength(2048)]),
        });
    }

    extractMaterialInfo(){
        this.module?.materials?.forEach((material) => {
            if(this.materialId === material?.id) {
                this.material = material;
                this.currentFile = {
                    mime: material.mime ?? '',
                    name: material.name ?? '',
                    url: material.filename ?? '',
                }
            }
        })


    }

    onSubmit(event?: any): void {
        if (this.loading) {
            return;
        }

        this.form?.get('title')?.setValue((this.form?.get('title')?.value || '').trim());
        this.form?.get('description')?.setValue((this.form?.get('description')?.value || '').trim());
        this.form?.updateValueAndValidity();

        if (this.form?.invalid) {
            this.errors = this.form?.controls;
            this.messageService.add({
                severity: 'error',
                summary: translate('Грешка!'),
                detail: translate('Моля, проверете полетата отново!')
            });
            this.loadingQueue--;
            return;
        }

        const formData: any = {
            title: this.form?.get('title')?.value,
            description: this.form?.get('description')?.value,
            material: this.currentFile,
        };

        const action = this.mode === 'edit'
        ? this.groupService.updateMaterial(this.groupId, this.module?.module?.id ?? 0, this.materialId, formData)
        : this.groupService.addMaterial(this.groupId, this.module?.module?.id ?? 0, formData);

        const subscription = action.subscribe({
            next: (data: any) => {
                this.loadingQueue--;

                this.messageService.add({
                    severity: 'success',
                    detail: translate('Успешно добавен запис'),
                });
                this.onAction.emit({action: 'send'});
            },
            error: (error: any) => {
                this.loadingQueue--;
                this.apiValidation.checkBadRequest(error, this.form).then((data: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: translate('Грешка!'),
                        detail: data.message
                    });
                    this.errors = data.errors;
                });
            }
        });

        this.subscriptions.push(subscription);
    }

    abortSubmit(): void {
        this.onAction.emit({action: 'close'});
    }


    uploadMaterial({files}: {files: UploadComponentQueue[]}): void {
        if (!files || files.length === 0) {
            this.messageFiles = 'No file selected or file is too large.';
            this.messageFilesType = 'error';
            return;
        }

        const fileData = files[0]; // Assuming only one file is selected
        this.currentFile = {
            name: fileData.file.name,
            url: fileData.response.data.path.replace('/uploads/files/', ''),
            mime: fileData.file.type,
        };
    }


}
